import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Check,
  GripVertical,
  Calendar,
  ChevronDown,
  ChevronRight,
  ListTodo,
  Paperclip,
  Link as LinkIcon,
  Timer,
  CalendarClock,
  Repeat,
} from "lucide-react";
import TaskCardMenu from "./TaskCardMenu";
import SortableBoardSubtaskRow from "./SortableBoardSubtaskRow";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { reorderVisibleInFullList } from "../../lib/reorderVisibleInFullList";
import { CSS } from "@dnd-kit/utilities";
import { useNavigate } from "@tanstack/react-router";
import { Task } from "../../types";
import Tooltip from "../ui/Tooltip";
import { getDaysAgo, formatTaskDuration, splitGraphemes } from "../../lib/utils";
import { getRecurrenceLabel } from "../../lib/recurrence";
import { formatLocalDate } from "../../lib/workingHours";
import { todayBoardFocus, visibleBoardSubtasks } from "../../lib/todayBoardFocus";
import { useUIPreferences } from "../../contexts/UIPreferencesContext";
import { useView } from "../../contexts/ViewContext";
import { useFilters } from "../../hooks/queries/useFilters";
import { useGoogleCalendar } from "../../contexts/useGoogleCalendar";
import { useArchiveTask } from "../../hooks/mutations/useArchiveTask";
import { useDeleteTask } from "../../hooks/mutations/useDeleteTask";
import { useReorderSubtasks } from "../../hooks/mutations/useReorderSubtasks";
import { useToggleSubtask } from "../../hooks/mutations/useToggleSubtask";
import TaskListEmoji from "./TaskListEmoji";

const GoogleCalendarIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M18 3h-1V2a1 1 0 0 0-2 0v1H9V2a1 1 0 0 0-2 0v1H6a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3h12a3 3 0 0 0 3-3V6a3 3 0 0 0-3-3z"
      fill="#4285F4"
    />
    <rect x="5" y="9" width="14" height="10" rx="1" fill="white" />
    <rect x="7" y="11" width="3" height="2.5" rx="0.5" fill="#EA4335" />
    <rect x="10.5" y="11" width="3" height="2.5" rx="0.5" fill="#34A853" />
    <rect x="14" y="11" width="3" height="2.5" rx="0.5" fill="#FBBC04" />
    <rect x="7" y="14.5" width="3" height="2.5" rx="0.5" fill="#4285F4" />
    <rect x="10.5" y="14.5" width="3" height="2.5" rx="0.5" fill="#EA4335" />
    <rect x="14" y="14.5" width="3" height="2.5" rx="0.5" fill="#34A853" />
  </svg>
);

// ── Task completion exit animation ("Confetti Boom") ──────────────────────
// Total ms the card stays mounted after a click before we fire onToggle and
// the parent unmounts it. Must be ≥ the longest animation (card launch 560ms
// + grid collapse starting at 300ms with 320ms duration = 620ms).
const EXIT_DURATION_MS = 620;

const CONFETTI_COLORS = [
  "var(--color-primary)",
  "var(--color-primary-light)",
  "var(--color-primary-dark)",
  "#e8a86a",
  "#c96f4a",
  "#f5e6c8",
];
const CONFETTI_SHAPES = ["rect", "circle", "tri"] as const;
type ConfettiShape = (typeof CONFETTI_SHAPES)[number];

interface Particle {
  tx: number;
  ty: number;
  rot: number;
  delay: number;
  color: string;
  shape: ConfettiShape;
}

function generateConfetti(count = 36): Particle[] {
  return Array.from({ length: count }, () => {
    const angle = Math.random() * Math.PI * 2;
    const distance = 70 + Math.random() * 110;
    return {
      tx: Math.cos(angle) * distance,
      ty: Math.sin(angle) * distance,
      rot: Math.random() * 1080 - 540,
      delay: Math.random() * 40,
      color:
        CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      shape:
        CONFETTI_SHAPES[Math.floor(Math.random() * CONFETTI_SHAPES.length)],
    };
  });
}

interface TaskCardProps {
  task: Task;
  viewMode: string;
  suppressTransform?: boolean;
  /** Owned by the shell: completing a task with open sub-tasks confirms first. */
  onToggle: (id: string) => void;
  onEdit: (task: Task) => void;
  onConvertToSubtask?: (id: string) => void;
  // Called right before onToggle fires at the end of the exit animation.
  // Gives the parent list a chance to re-baseline its FLIP animation so it
  // doesn't snap the sibling back to its pre-click position and replay the
  // upward animation (the "double-bounce").
  onBeforeExit?: () => void;
  // External signal to run the exit animation (used when the parent
  // dialog — e.g. "complete with subtasks" — confirms completion after
  // the checkbox click was deferred). Flipping this to true kicks off
  // the same animation path as a direct checkbox click.
  requestExit?: boolean;
  // Keyboard is on this card; the ring paints only while it also holds focus.
  isFocused?: boolean;
}

const TaskCard: React.FC<TaskCardProps> = ({
  task,
  viewMode,
  suppressTransform,
  onToggle,
  onEdit,
  onConvertToSubtask,
  onBeforeExit,
  requestExit,
  isFocused,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [hoveringCheckbox, setHoveringCheckbox] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [particles, setParticles] = useState<Particle[]>([]);
  const [burstOrigin, setBurstOrigin] = useState({ x: 0, y: 0 });
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const checkboxRef = useRef<HTMLButtonElement | null>(null);
  const { preferences } = useUIPreferences();
  const { showCompleted, allSubtasksExpanded, activeFilterId, searchTerm } = useView();
  const { data: filters } = useFilters();
  const { isConnected: isGoogleCalendarConnected } = useGoogleCalendar();
  const { deleteTask } = useDeleteTask();
  const { archiveTask } = useArchiveTask();
  const toggleSubtask = useToggleSubtask();
  const reorderSubtasks = useReorderSubtasks();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigate = useNavigate() as any;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id, data: { type: "task", task } });

  // Measure burst origin, spawn confetti, play exit animation, then fire
  // onToggle so the parent removes the task from its list.
  const startExitAnimation = useCallback(() => {
    if (isExiting) return;
    const wrapperRect = wrapperRef.current?.getBoundingClientRect();
    const checkboxRect = checkboxRef.current?.getBoundingClientRect();
    if (wrapperRect && checkboxRect) {
      setBurstOrigin({
        x: checkboxRect.left - wrapperRect.left + checkboxRect.width / 2,
        y: checkboxRect.top - wrapperRect.top + checkboxRect.height / 2,
      });
    }
    setParticles(generateConfetti());
    setHoveringCheckbox(false);
    setIsExiting(true);
    window.setTimeout(() => {
      onBeforeExit?.();
      onToggle(task.id);
    }, EXIT_DURATION_MS);
  }, [isExiting, onBeforeExit, onToggle, task.id]);

  // Click → either open the "complete with incomplete subtasks" dialog
  // (handled by the parent via onToggle) or play the exit animation before
  // firing onToggle. Un-checking (task already completed) skips animation.
  const handleCheckboxClick = () => {
    if (task.completed) {
      onToggle(task.id);
      return;
    }
    if (isExiting) return;
    const hasIncompleteSubtasks = !!task.subtasks?.some((s) => !s.completed);
    if (hasIncompleteSubtasks) {
      // Defer the animation: parent opens a confirmation dialog. If the
      // user confirms, the parent flips `requestExit` to play the exit
      // animation at that point.
      onToggle(task.id);
      return;
    }
    startExitAnimation();
  };

  // External trigger from dialog confirmation flow.
  useEffect(() => {
    if (requestExit && !isExiting && !task.completed) {
      startExitAnimation();
    }
  }, [requestExit, isExiting, task.completed, startExitAnimation]);

  // While hovering the checkbox (and not already exiting), hint that a
  // click will complete this task: fade the card and show a preview check.
  const showHoverPreview = hoveringCheckbox && !task.completed && !isExiting;

  const style = {
    transform: suppressTransform ? undefined : CSS.Transform.toString(transform),
    transition: suppressTransform ? undefined : transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  const subtasks = useMemo(() => task.subtasks || [], [task.subtasks]);
  const hasSubtasks = subtasks.length > 0;
  const activeDateFilters = filters?.find((filter) => filter.id === activeFilterId)?.date_filters;
  const today = formatLocalDate(new Date());
  const dateFocus = useMemo(
    () =>
      todayBoardFocus({
        searchTerm,
        activeFilterId,
        dateFilters: activeDateFilters,
        scheduledDate: task.scheduled_date,
        completed: task.completed,
        today,
      }),
    [searchTerm, activeFilterId, activeDateFilters, task.scheduled_date, task.completed, today],
  );

  const subtaskSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );
  const filteredSubtasks = useMemo(
    () => visibleBoardSubtasks(subtasks, showCompleted, dateFocus, today),
    [subtasks, showCompleted, dateFocus, today],
  );
  const subtaskIds = useMemo(
    () => filteredSubtasks.map((st) => st.id),
    [filteredSubtasks],
  );
  const handleSubtaskDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const next = reorderVisibleInFullList(
      subtasks,
      subtaskIds,
      String(active.id),
      String(over.id),
    );
    if (next === subtasks) return;
    reorderSubtasks(task.id, next);
  };

  // Opening a sub-task is a route change, not a parent concern: deep-link to
  // the task with that sub-task selected, keeping the current search params
  // (the active filters) so closing the dialog returns to the same board.
  const openSubtask = useCallback(
    (subtaskId: string) => {
      navigate({
        to: "/task/$taskId",
        params: { taskId: task.id },
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          subtask: subtaskId,
        }),
      });
    },
    [navigate, task.id],
  );
  // Sync local expansion state with global toggle
  useEffect(() => {
    if (hasSubtasks) {
      setIsExpanded(allSubtasksExpanded);
    }
  }, [allSubtasksExpanded, hasSubtasks]);
  // A Today filter is only useful when the matching sub-task is on screen.
  useEffect(() => {
    if (dateFocus.active && filteredSubtasks.length > 0) {
      setIsExpanded(true);
    }
  }, [dateFocus.active, filteredSubtasks.length]);
  const completedSubtasks = subtasks.filter((st) => st.completed).length;
  const attachments = task.attachments || [];
  const hasAttachments = attachments.length > 0;
  const links = task.links || [];
  const hasLinks = links.length > 0;
  const hasDuration = task.duration != null && task.duration > 0;
  const taskTags = task.tags || [];
  const hasTags = taskTags.length > 0;
  const showScheduleDates = preferences.showScheduleDatesChip;
  const showSubtasks = preferences.showSubtasksChip && hasSubtasks;
  const showAttachments = preferences.showAttachmentsChip && hasAttachments;
  const showLinks = preferences.showLinksChip && hasLinks;
  const showDuration = preferences.showDurationChip && hasDuration;
  const showTags = preferences.showTagsChip && hasTags;
  const showCreatedDate = preferences.showCreatedDateChip && task.created_at;

  return (
    <div
      className={`task-collapse space-y-0 outline-none group/card ${isExiting ? "task-collapse-active" : ""}`}
      ref={(el) => {
        setNodeRef(el);
        wrapperRef.current = el;
      }}
      style={style}
      data-task-id={task.id}
      tabIndex={-1}
    >
      <div className="task-collapse-inner">
        <div
          data-today-context={dateFocus.parentOutsideToday ? "outside" : undefined}
          className={`bg-warm-bg-card p-3 rounded-card border transition-all duration-300 ease-out group ${
            task.completed
              ? "border-warm-border-subtle opacity-60"
              : dateFocus.parentOutsideToday
                ? "border-warm-border shadow-soft-md opacity-50"
                : "border-warm-border shadow-soft-md hover:shadow-soft-lg"
          } ${!task.completed && task.priority === 1 ? "border-l-4 border-l-red-700" : ""} ${!task.completed && task.priority === 2 ? "border-l-4 border-l-orange-500" : ""} ${!task.completed && task.priority === 3 ? "border-l-4 border-l-yellow-400" : ""} ${showHoverPreview ? "task-card-hover-preview" : ""} ${isExiting ? "task-exit-boom" : ""} ${isFocused ? "group-focus/card:ring-2 group-focus/card:ring-inset group-focus/card:ring-warm-blue" : ""}`}
        >
          {dateFocus.parentOutsideToday && <span className="sr-only">Not for today</span>}
          <div className="flex items-start gap-2.5">
          <div
            className="flex-shrink-0 text-warm-text-tertiary cursor-grab active:cursor-grabbing mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150 ease-in-out"
            {...attributes}
            {...listeners}
            tabIndex={-1}
          >
            <GripVertical size={16} />
          </div>

          <button
            ref={checkboxRef}
            onClick={handleCheckboxClick}
            onMouseEnter={() => setHoveringCheckbox(true)}
            onMouseLeave={() => setHoveringCheckbox(false)}
            onFocus={() => setHoveringCheckbox(true)}
            onBlur={() => setHoveringCheckbox(false)}
            aria-label={task.completed ? "Mark as not done" : "Mark as done"}
            className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-150 ease-in-out mt-0.5 ${
              task.completed
                ? "bg-warm-blue border-warm-blue"
                : showHoverPreview
                  ? "bg-warm-blue/25 border-warm-blue scale-110 shadow-soft-sm"
                  : "border-warm-text-tertiary hover:border-warm-blue"
            }`}
          >
            {task.completed && <Check size={12} className="text-white" />}
            {showHoverPreview && (
              <Check
                size={12}
                strokeWidth={3}
                className="text-warm-blue opacity-80"
              />
            )}
          </button>

          <div
            className="flex-1 min-w-0 cursor-pointer"
            onClick={() => { wrapperRef.current?.focus({ preventScroll: true }); onEdit(task); }}
          >
            <Tooltip content={task.title} className="block min-w-0" onlyWhenTruncated>
              <p
                aria-label={task.title}
                dir="auto"
                className={`text-sm leading-relaxed truncate transition-colors duration-150 ease-in-out ${
                  task.completed
                    ? "line-through text-warm-text-tertiary"
                    : "text-warm-text-primary"
                } ${showHoverPreview ? "title-dissolve-active" : ""}`}
              >
                {viewMode === "priority" && (
                  <TaskListEmoji listId={task.list_id} emoji={task.listEmoji} />
                )}
                {task.completed
                  ? task.title
                  : splitGraphemes(task.title).map((ch, i, arr) => (
                      <span
                        key={i}
                        className="dissolve-char"
                        style={
                          {
                            "--char-delay": `${(arr.length - 1 - i) * 22}ms`,
                          } as React.CSSProperties
                        }
                      >
                        {ch === " " ? "\u00A0" : ch}
                      </span>
                    ))}
              </p>
            </Tooltip>
            {((showScheduleDates && task.scheduled_date) ||
              showSubtasks ||
              showAttachments ||
              showLinks ||
              showDuration ||
              showTags ||
              showCreatedDate) && (
              <div className="flex items-center gap-2 mt-2 text-xs">
                {showScheduleDates && task.scheduled_date && (
                  <span
                    className={`flex items-center gap-1 px-2 py-0.5 rounded ${
                      isGoogleCalendarConnected
                        ? "bg-warm-bg-elevated text-warm-blue-dark dark:text-warm-blue"
                        : "bg-warm-bg-elevated text-warm-text-secondary"
                    }`}
                  >
                    {isGoogleCalendarConnected ? (
                      <GoogleCalendarIcon />
                    ) : (
                      <Calendar size={11} />
                    )}
                    {task.recurrence_frequency && (
                      <span className="flex items-center gap-1">
                        <Repeat size={11} />
                        <span>{getRecurrenceLabel(task)}&nbsp;·</span>
                      </span>
                    )}
                    {new Date(
                      task.scheduled_date + "T00:00:00",
                    ).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                    {task.persistent && (
                      <CalendarClock size={11} className="ml-0.5" />
                    )}
                  </span>
                )}
                {showSubtasks && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsExpanded(!isExpanded);
                    }}
                    className="flex items-center gap-1 px-2 py-0.5 bg-warm-bg-elevated text-warm-text-secondary hover:bg-warm-border-subtle rounded transition-colors duration-150 ease-in-out"
                  >
                    {isExpanded ? (
                      <ChevronDown size={11} />
                    ) : (
                      <ChevronRight size={11} />
                    )}
                    <ListTodo size={11} />
                    <span>
                      {completedSubtasks}/{subtasks.length}
                    </span>
                  </button>
                )}
                {showAttachments && (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-warm-bg-elevated text-warm-text-secondary rounded">
                    <Paperclip size={11} />
                    {attachments.length}
                  </span>
                )}
                {showLinks && (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-warm-bg-elevated text-warm-text-secondary rounded">
                    <LinkIcon size={11} />
                    {links.length}
                  </span>
                )}
                {showDuration && (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-warm-bg-elevated text-warm-text-secondary rounded">
                    <Timer size={11} />
                    {formatTaskDuration(task.duration)}
                  </span>
                )}
                {showTags &&
                  taskTags.map((tag) => (
                    <span
                      key={tag.id}
                      className="flex items-center gap-1 px-2 py-0.5 bg-warm-bg-elevated text-warm-text-secondary rounded"
                    >
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: tag.color }}
                      />
                      {tag.name}
                    </span>
                  ))}
                {showCreatedDate &&
                  (() => {
                    const { label, tooltip } = getDaysAgo(task.created_at!);
                    return (
                      <Tooltip content={tooltip}>
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-warm-bg-elevated text-warm-text-secondary rounded">
                          {label}
                        </span>
                      </Tooltip>
                    );
                  })()}
              </div>
            )}
          </div>

          <div
            className={`flex-shrink-0 flex items-center mt-0.5 transition-opacity duration-150 ease-in-out ${menuOpen ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus-within:opacity-100"}`}
          >
            <TaskCardMenu
              task={task}
              onDelete={() => deleteTask(task.id)}
              onArchive={() => archiveTask(task.id)}
              onConvertToSubtask={
                onConvertToSubtask &&
                !hasSubtasks &&
                !task.recurrence_frequency
                  ? () => onConvertToSubtask(task.id)
                  : undefined
              }
              onOpenChange={setMenuOpen}
            />
          </div>
        </div>
      </div>

      {/* Subtasks — smooth expand/collapse via CSS grid */}
      {filteredSubtasks.length > 0 && (
        <div className="subtask-panel ml-7 mt-[-1px]" data-expanded={isExpanded}>
          <div>
            <div className="bg-warm-bg-elevated border border-t-0 border-warm-border rounded-b-card overflow-hidden">
              <DndContext
                sensors={subtaskSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleSubtaskDragEnd}
              >
                <SortableContext
                  items={subtaskIds}
                  strategy={verticalListSortingStrategy}
                >
                  {filteredSubtasks.map((subtask, index) => (
                    <SortableBoardSubtaskRow
                      key={subtask.id}
                      subtask={subtask}
                      isLast={index === filteredSubtasks.length - 1}
                      onToggleSubTask={toggleSubtask}
                      onSubtaskClick={openSubtask}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          </div>
        </div>
      )}
      </div>
      {isExiting && (
        <div
          className="boom-overlay"
          style={{ left: burstOrigin.x, top: burstOrigin.y }}
          aria-hidden
        >
          <div className="boom-flash" />
          {particles.map((p, i) => (
            <span
              key={i}
              className={`confetti-particle confetti-${p.shape}`}
              style={
                {
                  "--tx": `${p.tx}px`,
                  "--ty": `${p.ty}px`,
                  "--rot": `${p.rot}deg`,
                  "--delay": `${p.delay}ms`,
                  "--color": p.color,
                } as React.CSSProperties
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TaskCard;
